import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../utils/config';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import ReportPreview from './ReportPreview';
import ReportJobs from './ReportJobs';
import ReportDefinitionForm from './ReportDefinitionForm';

const Reports = ({ selectedReportKey, onReportSelect }) => {
  const [definitions, setDefinitions] = useState([]);
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('preview'); // 'preview' or 'jobs'
  const [showDefineForm, setShowDefineForm] = useState(false);

  useEffect(() => {
    fetchDefinitions();
  }, []);

  useEffect(() => {
    if (selectedReportKey) {
      const def = definitions.find(d => d.key === selectedReportKey);
      if (def) {
        setSelectedDefinition(def);
      }
    }
  }, [selectedReportKey, definitions]);

  const fetchDefinitions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.REPORTS_DEFINITIONS_LIST, { headers });
      
      // Handle standardized response format: { status: "ok", data: [...] }
      // or direct array/object format
      let data = [];
      if (response.data?.status === 'ok' && response.data?.data) {
        data = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else {
        data = response.data?.results || response.data?.data || [];
      }
      
      setDefinitions(data);
      
      // If a report is selected, find and set it
      if (selectedReportKey) {
        const def = data.find(d => d.key === selectedReportKey);
        if (def) {
          setSelectedDefinition(def);
        }
      }
    } catch (err) {
      console.error('Error fetching report definitions - Full Error:', err);
      console.error('Error Response Status:', err?.response?.status);
      console.error('Error Response Data:', err?.response?.data);
      
      // Check if it's a 404 or 500 error (API not implemented)
      if (err?.response?.status === 404) {
        setError(`API endpoint not found (404). The Reports API endpoint ${API_ENDPOINTS.REPORTS_DEFINITIONS_LIST} needs to be implemented on the backend.`);
      } else if (err?.response?.status === 500) {
        setError('Server error (500). The Reports API endpoint exists but encountered an error.');
      } else if (err?.response?.status === 403) {
        setError('Permission denied (403). You may not have access to view reports.');
      } else if (err?.response?.status === 401) {
        setError('Authentication required (401). Please log in again.');
      } else if (err?.code === 'ERR_NETWORK' || err?.message?.includes('Network Error')) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        const errorMsg = err?.response?.data?.message || 
                         err?.response?.data?.detail || 
                         err?.message || 
                         'Failed to fetch report definitions';
        setError(errorMsg);
      }
      // Set empty array so it shows the "no reports" message instead of error
      setDefinitions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportSelect = (definition) => {
    setSelectedDefinition(definition);
    if (onReportSelect) {
      onReportSelect(definition.key);
    }
  };

  if (isLoading && definitions.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Loading reports...</div>
      </div>
    );
  }

  if (error && definitions.length === 0) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="AlertCircle" size={20} className="text-yellow-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-yellow-900 mb-2">Reports API Not Available</h4>
            <p className="text-sm text-yellow-800 mb-4">{error}</p>
            <Button
              variant="outline"
              onClick={fetchDefinitions}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showDefineForm && (
        <ReportDefinitionForm
          onClose={() => setShowDefineForm(false)}
          onSuccess={() => {
            setShowDefineForm(false);
            fetchDefinitions();
          }}
        />
      )}

      {/* Report Selection */}
      {!selectedDefinition && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Report Templates</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Select a template to generate a report</p>
            </div>
            <Button
              onClick={() => setShowDefineForm(true)}
              iconName="Plus"
              iconPosition="left"
              size="sm"
            >
              New Template
            </Button>
          </div>
          {definitions.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-40 text-muted-foreground" />
              <p className="text-muted-foreground font-medium mb-1">No report templates yet.</p>
              <p className="text-sm text-muted-foreground mb-6">Create a new template to get started.</p>
              <div className="flex items-center justify-center space-x-2">
                <Button
                  variant="outline"
                  onClick={fetchDefinitions}
                  iconName="RefreshCw"
                  iconPosition="left"
                  size="sm"
                >
                  Refresh
                </Button>
                <Button
                  onClick={() => setShowDefineForm(true)}
                  iconName="Plus"
                  iconPosition="left"
                  size="sm"
                >
                  New Template
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {definitions.map((def) => (
                <button
                  key={def.key}
                  onClick={() => handleReportSelect(def)}
                  className="p-4 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
                >
                  <div className="flex items-start space-x-3">
                    <Icon name="FileText" size={24} color="var(--color-primary)" />
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{def.name}</h4>
                      {def.description && (
                        <p className="text-sm text-muted-foreground mt-1">{def.description}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Selected Report View */}
      {selectedDefinition && (
        <div className="space-y-6">
          {/* Report Header */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-foreground">{selectedDefinition.name}</h2>
                {selectedDefinition.description && (
                  <p className="text-muted-foreground mt-1">{selectedDefinition.description}</p>
                )}
              </div>
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedDefinition(null);
                  if (onReportSelect) {
                    onReportSelect(null);
                  }
                }}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Tabs */}
            <div className="flex space-x-2 mt-4 border-b border-border">
              <button
                onClick={() => setActiveTab('preview')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'preview'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Preview
              </button>
              <button
                onClick={() => setActiveTab('jobs')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'jobs'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Generated Reports
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'preview' && (
            <ReportPreview
              reportKey={selectedDefinition.key}
              inputSchema={selectedDefinition.input_schema}
            />
          )}

          {activeTab === 'jobs' && (
            <ReportJobs reportKey={selectedDefinition.key} />
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;

